import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { allBlogArticles, blogCategories } from '../../mocks/blogArticles';
import BlogCard from './components/BlogCard';
import CategoryFilter from './components/CategoryFilter';
import NewsletterSignup from './components/NewsletterSignup';

const BlogPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeCity, setActiveCity] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useDocumentMeta({
    title: 'Tax Tips Blog - Freelancer Taxes, Retirement Planning & Remote Work Tax Guide 2026',
    description: 'Expert tax tips for freelancers, remote workers, and retirement planning in every major US and UK city. Maximize your take-home pay with location-specific tax strategies updated for 2026.',
    keywords: 'tax tips, freelancer taxes, remote work taxes, retirement tax planning, city tax guide, take-home pay tips, salary tax blog, self-employment tax, 1099 taxes 2026',
    canonical: 'https://mysalarycalculator.net/blog',
    ogTitle: 'Tax Tips Blog - Freelancer Taxes, Retirement Planning & Remote Work Tax Guide 2026',
    ogDescription: 'Expert tax tips for freelancers, remote workers, and retirees in every major US and UK city. Maximize your take-home pay with location-specific advice.',
    ogUrl: 'https://mysalarycalculator.net/blog',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Tax Tips Blog - Freelancer, Retirement & Remote Work Tax Strategies',
    twitterDescription: 'Expert tax tips for every major city. Freelancer taxes, retirement planning, and remote work tax implications.',
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Salary Tax Calculator Blog",
        "description": "City-specific tax tips covering freelancer taxes, retirement planning, remote work tax implications, and financial planning strategies",
        "url": "https://mysalarycalculator.net/blog",
        "publisher": {
          "@type": "Organization",
          "name": "Salary Tax Calculator"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mysalarycalculator.net/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://mysalarycalculator.net/blog" }
        ]
      }
    ]
  });

  const cities = useMemo(() => {
    const citySet = new Set(allBlogArticles.map((a) => a.city));
    return Array.from(citySet).sort();
  }, []);

  const filteredArticles = useMemo(() => {
    return allBlogArticles.filter((article) => {
      const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
      const matchesCity = activeCity === 'All' || article.city === activeCity;
      const matchesSearch =
        searchQuery === '' ||
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesCity && matchesSearch;
    });
  }, [activeCategory, activeCity, searchQuery]);

  const featuredArticles = allBlogArticles.filter((a) => a.featured).slice(0, 3);
  const regularArticles = filteredArticles.filter((a) => !a.featured || activeCategory !== 'All' || activeCity !== 'All' || searchQuery !== '');

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" role="banner">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <a href="/" aria-label="Salary Tax Calculator Home">
                <img
                  src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
                  alt="Salary Tax Calculator Logo"
                  className="h-10 w-auto cursor-pointer"
                  width="40"
                  height="40"
                />
              </a>
              <div className="hidden md:block">
                <span className="text-xl font-bold text-gray-900">
                  <a href="/" className="hover:text-teal-600 transition-colors cursor-pointer">Salary Tax Calculator</a>
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <a href="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">Calculator</a>
              <a href="/blog" className="text-sm font-medium text-teal-600 cursor-pointer hidden md:block">Blog</a>
              <a href="/states" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">US States</a>
              <a href="/usa/ny/manhattan" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">Manhattan</a>
              <a href="/usa/ca/san-francisco" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">San Francisco</a>
            </div>
          </div>
        </nav>
      </header>

      <main role="main">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-2">
          <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-home-4-line text-xs"></i>
              Home
            </a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">Blog</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <img
              src="https://public.readdy.ai/ai/img_res/8e08583e2267a341267a609cf29d2bd9.jpg"
              alt="Tax tips blog background"
              className="w-full h-full object-cover opacity-20"
              width="1600"
              height="600"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/40 to-gray-900/80" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/20 border border-teal-500/30 rounded-full text-teal-300 text-sm font-medium mb-6">
              <i className="ri-article-line"></i> Tax Insights &amp; Strategies
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              <strong>Smart Tax Tips &amp; Salary Guides for Every City</strong>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-4">
              Location-specific tax strategies to <strong>maximize your take-home pay</strong>. Expert advice on freelancer taxes, retirement planning, and remote work tax implications.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-gray-300 whitespace-nowrap">Freelancer Taxes</span>
              <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-gray-300 whitespace-nowrap">Retirement Planning</span>
              <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-gray-300 whitespace-nowrap">Remote Work</span>
              <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-gray-300 whitespace-nowrap">Tax Strategy</span>
              <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-gray-300 whitespace-nowrap">Relocation</span>
            </div>
            <div className="max-w-lg mx-auto relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search articles by city, topic, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-teal-400 focus:bg-white/15 transition-all"
              />
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        {activeCategory === 'All' && activeCity === 'All' && searchQuery === '' && (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900"><strong>Featured Tax Strategy Articles</strong></h2>
                  <p className="text-gray-500 text-sm mt-1">Our most popular tax guides for maximizing take-home pay</p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <BlogCard key={article.id} article={article} featured />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Filters + Articles Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <CategoryFilter
              categories={blogCategories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              cities={cities}
              activeCity={activeCity}
              onCityChange={setActiveCity}
            />

            <div className="mt-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {activeCategory === 'All' && activeCity === 'All' && searchQuery === ''
                    ? 'All Tax &amp; Salary Articles'
                    : `${filteredArticles.length} Article${filteredArticles.length !== 1 ? 's' : ''} Found`}
                </h2>
              </div>

              {filteredArticles.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-4">
                    <i className="ri-article-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No articles found</h3>
                  <p className="text-gray-500 text-sm">Try adjusting your filters or search query.</p>
                  <button
                    onClick={() => { setActiveCategory('All'); setActiveCity('All'); setSearchQuery(''); }}
                    className="mt-4 px-5 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(activeCategory !== 'All' || activeCity !== 'All' || searchQuery !== ''
                    ? filteredArticles
                    : regularArticles
                  ).map((article) => (
                    <BlogCard key={article.id} article={article} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Popular Calculators CTA */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center"><strong>Try Our Free Salary Tax Calculators</strong></h2>
            <p className="text-gray-500 text-sm text-center mb-8">See exactly how these tax tips affect your take-home pay in each city</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[
                { name: 'Manhattan, NY', path: '/usa/ny/manhattan' },
                { name: 'San Francisco, CA', path: '/usa/ca/san-francisco' },
                { name: 'Austin, TX', path: '/usa/tx/austin' },
                { name: 'Chicago, IL', path: '/usa/il/chicago' },
                { name: 'Miami, FL', path: '/usa/fl/miami' },
                { name: 'Seattle, WA', path: '/usa/wa/seattle' },
                { name: 'Boston, MA', path: '/usa/ma/boston' },
                { name: 'Denver, CO', path: '/usa/co/denver' },
                { name: 'Atlanta, GA', path: '/usa/ga/atlanta' },
                { name: 'Phoenix, AZ', path: '/usa/az/phoenix' },
                { name: 'Philadelphia, PA', path: '/usa/pa/philadelphia' },
                { name: 'London, UK', path: '/uk/london' },
              ].map((loc) => (
                <a
                  key={loc.path}
                  href={loc.path}
                  onClick={(e) => { e.preventDefault(); navigate(loc.path); }}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-teal-50 border border-gray-200 hover:border-teal-300 rounded-xl text-sm font-medium text-gray-700 hover:text-teal-700 transition-all cursor-pointer"
                >
                  <i className="ri-calculator-line text-gray-400"></i>
                  <span className="truncate">{loc.name}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <NewsletterSignup formUrl="https://readdy.ai/api/form/d69uhq7mvg9ih2c7a7c0" />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="grid grid-cols-1 md:grid-cols-4 gap-8" aria-label="Footer navigation">
            <div>
              <h3 className="text-lg font-bold mb-4">Salary Calculator</h3>
              <p className="text-gray-400 text-sm">
                Accurate tax calculations for locations worldwide. Plan your finances with confidence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4"><a href="/blog" className="hover:text-teal-400 transition-colors cursor-pointer">Popular Articles</a></h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/blog/manhattan-freelancer-taxes-2026" className="hover:text-white transition-colors cursor-pointer">NYC Freelancer Tax Guide</a></li>
                <li><a href="/blog/manhattan-remote-work-taxes" className="hover:text-white transition-colors cursor-pointer">Remote Work Tax Traps</a></li>
                <li><a href="/blog/manhattan-retirement-planning" className="hover:text-white transition-colors cursor-pointer">NYC Retirement Planning</a></li>
                <li><a href="/blog/austin-no-state-tax-guide" className="hover:text-white transition-colors cursor-pointer">Austin Zero Tax Guide</a></li>
                <li><a href="/blog/london-uk-tax-guide" className="hover:text-white transition-colors cursor-pointer">London UK Tax Guide</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Calculators</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/usa/ny/manhattan" className="hover:text-white transition-colors cursor-pointer">Manhattan, NY</a></li>
                <li><a href="/usa/ca/san-francisco" className="hover:text-white transition-colors cursor-pointer">San Francisco, CA</a></li>
                <li><a href="/uk/london" className="hover:text-white transition-colors cursor-pointer">London, UK</a></li>
                <li><a href="/usa/tx/austin" className="hover:text-white transition-colors cursor-pointer">Austin, TX</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="/disclaimer" className="hover:text-white transition-colors cursor-pointer">Disclaimer</a></li>
              </ul>
            </div>
          </nav>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2026 Salary Tax Calculator. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
